<template>
  <div class="mobile-calendar-page">
    <header class="mobile-calendar-bar">
      <button
        type="button"
        class="mobile-calendar-nav-btn"
        @click="goBackward"
        :aria-label="navPrevLabel"
      >
        <i class="bi-chevron-left"></i>
      </button>
      <button type="button" class="mobile-calendar-date" @click="openDatePicker">
        {{ headerLabel }}
      </button>
      <button
        type="button"
        class="mobile-calendar-nav-btn"
        @click="goForward"
        :aria-label="navNextLabel"
      >
        <i class="bi-chevron-right"></i>
      </button>
      <div class="dropdown mobile-calendar-mode-wrap">
        <button
          type="button"
          class="mobile-calendar-mode"
          data-bs-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          {{ modeLabel }}
        </button>
        <ul class="dropdown-menu dropdown-menu-end mobile-calendar-mode-menu">
          <li v-for="m in calendarModes" :key="m">
            <button
              type="button"
              class="dropdown-item"
              :class="{ active: viewMode === m }"
              @click="setViewMode(m)"
            >
              {{ calendarModeLabel(m) }}
            </button>
          </li>
        </ul>
      </div>
      <button
        v-if="showTodayButton"
        type="button"
        class="mobile-calendar-today"
        @click="goToday"
      >
        {{ $t("ui.today") }}
      </button>
      <div v-if="datepickerEnabled" class="mobile-calendar-datepicker-host">
        <datepicker
          id="mobile-calendar-date-picker-input"
          v-model="pickedDate"
          :locale="language"
          :weekStartsOn="weekStartOnMonday"
        />
      </div>
    </header>

    <p v-if="weekSyncError" class="mobile-calendar-sync-error" role="status">
      {{ weekSyncError }}
    </p>

    <div
      v-if="viewMode === 'day'"
      class="mobile-calendar-body"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <to-do-list
        v-if="selectedDay"
        :key="'day-' + selectedDay"
        :id="selectedDay"
        :showCustomList="showCustomList"
        mobile-layout
      />
    </div>

    <div
      v-else-if="viewMode === 'week'"
      ref="weekBody"
      class="mobile-calendar-body mobile-calendar-week"
      :class="
        weekOrientation === 'horizontal'
          ? 'mobile-calendar-week--horizontal'
          : 'mobile-calendar-week--vertical'
      "
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <section
        v-for="day in weekDayIds"
        :key="'week-' + day"
        class="mobile-week-day"
        :class="{ 'is-today': isToday(day) }"
        :data-day="day"
      >
        <to-do-list :id="day" :showCustomList="showCustomList" mobile-layout />
      </section>
    </div>

    <div
      v-else-if="viewMode === 'month'"
      class="mobile-calendar-body mobile-calendar-month"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <calendar-month-view
        :anchor-day="selectedDay"
        :week-starts-on-monday="weekStartsOnMondayBool"
        @select-day="onMonthSelectDay"
      />
    </div>

    <div
      v-else-if="viewMode === 'year'"
      class="mobile-calendar-body mobile-calendar-year"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <calendar-year-view
        :anchor-day="selectedDay"
        :week-starts-on-monday="weekStartsOnMondayBool"
        @select-month="onYearSelectMonth"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Datepicker from "vue3-datepicker";
import toDoList from "../components/toDoList";
import CalendarMonthView from "./CalendarMonthView.vue";
import CalendarYearView from "./CalendarYearView.vue";
import languageHelper from "../helpers/languageHelper.js";
const { shiftDay, isToday, weekDays, shiftWeek, weekStart, dayWindow } = require("./dayNav.js");
const {
  normalizeCalendarView,
  shiftMonth,
  shiftYear,
  monthGridDays,
  yearMonthStarts,
} = require("./calendarGrid.js");
const { swipeHorizontal } = require("./swipeHorizontal.js");
const {
  weekLayoutOrientation,
  subscribeMobileViewport,
} = require("./viewport.js");
const { applyWeekToStore } = require("../sync/sessionSync.js");
import repeatingEventHelper from "../helpers/repeatingEvents.js";

const CALENDAR_MODE_I18N = {
  day: "ui.calendarViewDay",
  week: "ui.calendarViewWeek",
  month: "ui.calendarViewMonth",
  year: "ui.calendarViewYear",
};

const WEEK_SYNC_DEBOUNCE_MS = 120;

export default {
  name: "MobileCalendarPage",
  components: {
    Datepicker,
    toDoList,
    CalendarMonthView,
    CalendarYearView,
  },
  props: {
    selectedDay: {
      type: String,
      default: null,
    },
  },
  emits: ["update:selectedDay"],
  data() {
    return {
      viewMode: "day",
      calendarModes: ["day", "week", "month", "year"],
      weekOrientation: "vertical",
      pickedDate: new Date(),
      datepickerEnabled: false,
      touchStartX: null,
      unsubscribeViewport: null,
      onWindowResize: null,
      viewportLayoutReady: false,
      weekSyncError: "",
      weekSyncTimer: null,
      weekSyncSeq: 0,
    };
  },
  created() {
    this.viewMode = normalizeCalendarView(
      this.$store.getters.config.defaultCalendarView
    );
  },
  computed: {
    modeLabel() {
      return this.calendarModeLabel(this.viewMode);
    },
    showCustomList() {
      return this.$store.getters.config.customList;
    },
    weekStartOnMonday() {
      return this.$store.getters.config.weekStartOnMonday ? 1 : 0;
    },
    weekStartsOnMondayBool() {
      return !!this.$store.getters.config.weekStartOnMonday;
    },
    language() {
      return languageHelper.getLanguagePack(this.$store.getters.config.language);
    },
    calendarColumns() {
      const n = parseInt(this.$store.getters.config.columns, 10);
      if (Number.isFinite(n) && n >= 1) return Math.min(7, n);
      return 5;
    },
    weekDayIds() {
      if (!this.selectedDay) return [];
      // Desktop horizontal: sliding N-day window (default 5); pan to reveal weekends
      if (this.weekOrientation === "horizontal") {
        return dayWindow(this.selectedDay, this.calendarColumns);
      }
      return weekDays(this.selectedDay, this.weekStartsOnMondayBool);
    },
    usesSlidingDayWindow() {
      return this.viewMode === "week" && this.weekOrientation === "horizontal";
    },
    navPrevLabel() {
      if (this.viewMode === "month") return "上一月";
      if (this.viewMode === "year") return "上一年";
      if (this.viewMode === "day" || this.usesSlidingDayWindow) return "上一天";
      return "上一周";
    },
    navNextLabel() {
      if (this.viewMode === "month") return "下一月";
      if (this.viewMode === "year") return "下一年";
      if (this.viewMode === "day" || this.usesSlidingDayWindow) return "下一天";
      return "下一周";
    },
    headerLabel() {
      if (!this.selectedDay) return "";
      const lang = this.$store.getters.config.language;
      const isZh = String(lang || "").toLowerCase().startsWith("zh");
      if (this.viewMode === "month") {
        const m = moment(this.selectedDay, "YYYYMMDD").locale(lang);
        return isZh ? m.format("YYYY年M月") : m.format("MMM YYYY");
      }
      if (this.viewMode === "year") {
        const m = moment(this.selectedDay, "YYYYMMDD").locale(lang);
        return isZh ? m.format("YYYY年") : m.format("YYYY");
      }
      if (this.viewMode === "day") {
        return moment(this.selectedDay, "YYYYMMDD").locale(lang).format("LL");
      }
      if (this.weekDayIds.length < 1) return "";
      const start = moment(this.weekDayIds[0], "YYYYMMDD").locale(lang);
      const end = moment(this.weekDayIds[this.weekDayIds.length - 1], "YYYYMMDD").locale(lang);
      if (isZh) {
        if (start.isSame(end, "day")) return start.format("M月D日");
        if (start.isSame(end, "month")) {
          return `${start.format("M月D日")}-${end.format("D日")}`;
        }
        return `${start.format("M月D日")}-${end.format("M月D日")}`;
      }
      if (start.isSame(end, "day")) {
        return start.format("MMM D");
      }
      if (start.isSame(end, "month")) {
        return `${start.format("MMM D")} – ${end.format("D")}`;
      }
      return `${start.format("MMM D")} – ${end.format("MMM D")}`;
    },
    showTodayButton() {
      if (!this.selectedDay) return false;
      const today = moment().format("YYYYMMDD");
      if (this.viewMode === "day") return !isToday(this.selectedDay);
      if (this.viewMode === "month") {
        return this.selectedDay.slice(0, 6) !== today.slice(0, 6);
      }
      if (this.viewMode === "year") {
        return this.selectedDay.slice(0, 4) !== today.slice(0, 4);
      }
      return !this.weekDayIds.includes(today);
    },
  },
  methods: {
    isToday,
    calendarModeLabel(mode) {
      const key = CALENDAR_MODE_I18N[mode] || CALENDAR_MODE_I18N.day;
      return this.$t(key);
    },
    setViewMode(mode) {
      this.viewMode = normalizeCalendarView(mode);
      if (this.viewMode === "week") {
        this.$nextTick(() => this.scrollToTodayOrTop());
      }
    },
    applyViewportDefaults() {
      const width = window.innerWidth;
      this.weekOrientation = weekLayoutOrientation(width);
      this.viewportLayoutReady = true;
    },
    setDay(day) {
      if (!day || day === this.selectedDay) return;
      this.$emit("update:selectedDay", day);
    },
    goPrevDay() {
      if (!this.selectedDay) return;
      this.setDay(shiftDay(this.selectedDay, -1));
    },
    goNextDay() {
      if (!this.selectedDay) return;
      this.setDay(shiftDay(this.selectedDay, 1));
    },
    goPrevWeek() {
      if (!this.selectedDay) return;
      this.setDay(shiftWeek(this.selectedDay, -1, this.weekStartsOnMondayBool));
    },
    goNextWeek() {
      if (!this.selectedDay) return;
      this.setDay(shiftWeek(this.selectedDay, 1, this.weekStartsOnMondayBool));
    },
    goBackward() {
      if (!this.selectedDay) return;
      if (this.viewMode === "month") {
        this.setDay(shiftMonth(this.selectedDay, -1));
        return;
      }
      if (this.viewMode === "year") {
        this.setDay(shiftYear(this.selectedDay, -1));
        return;
      }
      if (this.viewMode === "day" || this.usesSlidingDayWindow) {
        this.goPrevDay();
        return;
      }
      this.goPrevWeek();
    },
    goForward() {
      if (!this.selectedDay) return;
      if (this.viewMode === "month") {
        this.setDay(shiftMonth(this.selectedDay, 1));
        return;
      }
      if (this.viewMode === "year") {
        this.setDay(shiftYear(this.selectedDay, 1));
        return;
      }
      if (this.viewMode === "day" || this.usesSlidingDayWindow) {
        this.goNextDay();
        return;
      }
      this.goNextWeek();
    },
    snapHorizontalWindowToWeekStart() {
      if (!this.selectedDay || !this.usesSlidingDayWindow) return;
      const start = weekStart(this.selectedDay, this.weekStartsOnMondayBool);
      if (start !== this.selectedDay) this.setDay(start);
    },
    goToday() {
      const today = moment().format("YYYYMMDD");
      if (this.usesSlidingDayWindow) {
        // Land on the week that contains today (Mon start → Mon–Fri first page)
        this.setDay(weekStart(today, this.weekStartsOnMondayBool));
      } else {
        this.setDay(today);
      }
      if (this.viewMode === "week") {
        this.$nextTick(() => this.scrollToTodayOrTop());
      }
    },
    scrollToTodayOrTop() {
      const today = moment().format("YYYYMMDD");
      const el = this.$el.querySelector(`[data-day="${today}"]`);
      if (el) {
        el.scrollIntoView({ block: "start", behavior: "smooth" });
        return;
      }
      if (this.$refs.weekBody) this.$refs.weekBody.scrollTop = 0;
    },
    openDatePicker() {
      this.datepickerEnabled = true;
      this.$nextTick(() => {
        const input = document.getElementById("mobile-calendar-date-picker-input");
        if (!input) return;
        input.click();
        input.focus();
        input.addEventListener("focusout", this.resetDatePicker);
        input.onkeydown = (evt) => {
          if (evt.keyCode === 27) input.blur();
        };
      });
    },
    resetDatePicker() {
      const input = document.getElementById("mobile-calendar-date-picker-input");
      if (input) {
        input.removeEventListener("focusout", this.resetDatePicker);
      }
      this.datepickerEnabled = false;
    },
    onTouchStart(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      this.touchStartX = touch ? touch.clientX : null;
    },
    onTouchEnd(event) {
      if (this.touchStartX == null || !this.selectedDay) return;
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      const direction = swipeHorizontal(this.touchStartX, touch.clientX);
      this.touchStartX = null;
      if (direction === "right") this.goBackward();
      else if (direction === "left") this.goForward();
    },
    scheduleWeekSync() {
      if (this.weekSyncTimer) clearTimeout(this.weekSyncTimer);
      this.weekSyncTimer = setTimeout(() => {
        this.weekSyncTimer = null;
        this.loadVisibleWeek();
      }, WEEK_SYNC_DEBOUNCE_MS);
    },
    monthGridDayIds() {
      if (!this.selectedDay) return [];
      return monthGridDays(this.selectedDay, this.weekStartsOnMondayBool).map(
        (c) => c.id
      );
    },
    onMonthSelectDay(dayId) {
      this.setViewMode("day");
      this.$emit("update:selectedDay", dayId);
    },
    onYearSelectMonth(dayId) {
      this.setViewMode("month");
      this.$emit("update:selectedDay", dayId);
    },
    async loadVisibleWeek() {
      if (!this.selectedDay) return;
      const token = ++this.weekSyncSeq;
      try {
        if (this.viewMode === "year") {
          for (const monthStart of yearMonthStarts(this.selectedDay)) {
            if (token !== this.weekSyncSeq) return;
            const cells = monthGridDays(monthStart, this.weekStartsOnMondayBool);
            await applyWeekToStore(
              this.$store,
              monthStart,
              this.weekStartsOnMondayBool,
              {
                force: true,
                days: cells.map((c) => c.id),
              }
            );
          }
          if (token !== this.weekSyncSeq) return;
          this.weekSyncError = "";
          return;
        }
        const days =
          this.viewMode === "month"
            ? this.monthGridDayIds()
            : this.viewMode === "week" && this.weekDayIds.length
              ? this.weekDayIds
              : this.viewMode === "day"
                ? [this.selectedDay]
                : undefined;
        await applyWeekToStore(this.$store, this.selectedDay, this.weekStartsOnMondayBool, {
          force: true,
          days,
        });
        if (token !== this.weekSyncSeq) return;
        this.weekSyncError = "";
        const healDays = Array.isArray(days) ? days : this.weekDayIds;
        for (const day of healDays) {
          repeatingEventHelper.generateRepeatingEventsIntances(day, this);
        }
      } catch (error) {
        if (token !== this.weekSyncSeq) return;
        this.weekSyncError = (error && error.message) || "请求失败";
      }
    },
    syncSelectedDates() {
      if (this.viewMode === "week") {
        this.$store.commit("updateSelectedDates", this.weekDayIds.slice());
        return;
      }
      if (this.viewMode === "month") {
        this.$store.commit("updateSelectedDates", this.monthGridDayIds());
        return;
      }
      if (this.viewMode === "year") {
        // Presence sync loads the year; selectedDates stays light (export/print)
        this.$store.commit(
          "updateSelectedDates",
          this.selectedDay ? [this.selectedDay] : []
        );
        return;
      }
      if (this.selectedDay) {
        this.$store.commit("updateSelectedDates", [this.selectedDay]);
      }
    },
  },
  watch: {
    selectedDay: {
      handler() {
        this.syncSelectedDates();
        this.scheduleWeekSync();
      },
      immediate: true,
    },
    viewMode() {
      this.syncSelectedDates();
      this.scheduleWeekSync();
    },
    usesSlidingDayWindow(active) {
      if (active) this.snapHorizontalWindowToWeekStart();
    },
    pickedDate(val) {
      if (this.datepickerEnabled) {
        const input = document.getElementById("mobile-calendar-date-picker-input");
        if (input) {
          input.removeEventListener("focusout", this.resetDatePicker);
        }
        this.datepickerEnabled = false;
        this.setDay(moment(val).format("YYYYMMDD"));
        this.pickedDate = new Date();
        if (this.viewMode === "week") {
          this.$nextTick(() => {
            const day = moment(val).format("YYYYMMDD");
            const el = this.$el.querySelector(`[data-day="${day}"]`);
            if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
          });
        }
      }
    },
    weekDayIds() {
      this.syncSelectedDates();
      if (this.viewMode !== "week") return;
      this.$nextTick(() => this.scrollToTodayOrTop());
    },
  },
  mounted() {
    this.applyViewportDefaults();
    this.unsubscribeViewport = subscribeMobileViewport(() => {
      this.applyViewportDefaults();
    });
    // CDP / some embeds update innerWidth without firing matchMedia
    this.onWindowResize = () => this.applyViewportDefaults();
    window.addEventListener("resize", this.onWindowResize);
  },
  beforeUnmount() {
    if (this.weekSyncTimer) {
      clearTimeout(this.weekSyncTimer);
      this.weekSyncTimer = null;
    }
    if (this.unsubscribeViewport) {
      this.unsubscribeViewport();
      this.unsubscribeViewport = null;
    }
    if (this.onWindowResize) {
      window.removeEventListener("resize", this.onWindowResize);
      this.onWindowResize = null;
    }
  },
};
</script>

<style scoped>
.mobile-calendar-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
}

.mobile-calendar-sync-error {
  margin: 0;
  padding: 8px 12px;
  font-size: var(--m-caption, 13px);
  color: #842029;
  background: #f8d7da;
  border-bottom: 1px solid rgba(132, 32, 41, 0.15);
  flex-shrink: 0;
}

.dark-theme .mobile-calendar-sync-error {
  color: #f5c2c7;
  background: rgba(248, 81, 73, 0.18);
  border-bottom-color: rgba(248, 81, 73, 0.25);
}

.mobile-calendar-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.18));
  position: relative;
  flex-shrink: 0;
  background: var(--m-surface, #fff);
}

.mobile-calendar-nav-btn,
.mobile-calendar-today,
.mobile-calendar-mode {
  border: none;
  background: transparent;
  color: #0d6efd;
  padding: 0;
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: var(--m-text-secondary, 15px);
  font-weight: 500;
  border-radius: 10px;
}

.mobile-calendar-nav-btn i {
  font-size: 20px;
}

.mobile-calendar-date {
  flex: 1 1 auto;
  border: none;
  background: transparent;
  color: inherit;
  font-size: var(--m-bar, 17px);
  font-weight: 650;
  letter-spacing: -0.02em;
  text-align: center;
  padding: 8px 4px;
  cursor: pointer;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-calendar-mode-wrap {
  flex: 0 0 auto;
}

.mobile-calendar-mode {
  min-width: 40px;
  border-radius: 10px;
  font-weight: 650;
  font-size: var(--m-caption, 13px);
  color: inherit;
  background: rgba(120, 120, 128, 0.12);
}

/* Compact 4-row mode menu with background selection. */
.mobile-calendar-mode-menu {
  min-width: 0;
  width: max-content;
  padding: 4px;
  border-radius: 10px;
}

.mobile-calendar-mode-menu .dropdown-item {
  display: block;
  min-height: 0;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: var(--m-caption, 13px);
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  color: inherit;
  background: transparent;
}

.mobile-calendar-mode-menu .dropdown-item:hover,
.mobile-calendar-mode-menu .dropdown-item:focus {
  background: rgba(120, 120, 128, 0.12);
  color: inherit;
}

.mobile-calendar-mode-menu .dropdown-item.active {
  background: rgba(13, 110, 253, 0.14);
  color: #0d6efd;
}

.dark-theme .mobile-calendar-mode-menu .dropdown-item:hover,
.dark-theme .mobile-calendar-mode-menu .dropdown-item:focus {
  background: rgba(120, 120, 128, 0.28);
}

.dark-theme .mobile-calendar-mode-menu .dropdown-item.active {
  background: rgba(10, 132, 255, 0.22);
  color: #0a84ff;
}

.dark-theme .mobile-calendar-mode {
  background: rgba(120, 120, 128, 0.24);
}

.dark-theme .mobile-calendar-nav-btn,
.dark-theme .mobile-calendar-today {
  color: #0a84ff;
}

.mobile-calendar-today {
  font-weight: 650;
  white-space: nowrap;
  padding: 0 8px;
  min-width: auto;
}

.mobile-calendar-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  touch-action: pan-y;
  padding: 8px 12px 20px;
  background: var(--m-page, #f2f2f7);
}

.mobile-calendar-body:not(.mobile-calendar-week):not(.mobile-calendar-month):not(.mobile-calendar-year) {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 8px 12px 12px;
}

.mobile-calendar-month {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 8px 10px 12px;
}

.mobile-calendar-year {
  overflow: auto;
  padding: 8px 10px 16px;
}

.mobile-calendar-body:not(.mobile-calendar-week):not(.mobile-calendar-month):not(.mobile-calendar-year)
  :deep(.to-do-list-container) {
  flex: 1 1 auto !important;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 0;
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  background: var(--m-surface, #fff);
  border-radius: var(--m-radius, 12px);
  padding: 12px 12px 6px;
  box-shadow: 0 0.5px 1px rgba(0, 0, 0, 0.04);
}

.mobile-calendar-week {
  padding: 8px 0 24px;
  background: var(--m-page, #f2f2f7);
}

.mobile-calendar-week--vertical {
  display: block;
}

.mobile-calendar-week--horizontal {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 8px;
  padding: 8px 12px 16px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.mobile-week-day {
  margin: 0 12px 12px;
  border-radius: var(--m-radius, 12px);
  overflow: hidden;
  background: var(--m-surface, #fff);
  border: 1px solid transparent;
  box-shadow: 0 0.5px 1px rgba(0, 0, 0, 0.04);
  padding: 8px 10px 4px;
}

.mobile-calendar-week--horizontal .mobile-week-day {
  flex: 1 1 0;
  min-width: 160px;
  max-height: 100%;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Wide browser: fill height, equal columns, no cramped 160px strip */
@media (min-width: 768px) {
  .mobile-calendar-bar {
    padding: 10px 20px;
    gap: 6px;
    max-width: 100%;
  }

  .mobile-calendar-date {
    font-size: 18px;
    font-weight: 700;
  }

  .mobile-calendar-body:not(.mobile-calendar-week):not(.mobile-calendar-month):not(.mobile-calendar-year) {
    padding: 16px 24px 24px;
  }

  .mobile-calendar-body:not(.mobile-calendar-week):not(.mobile-calendar-month):not(.mobile-calendar-year)
    :deep(.to-do-list-container) {
    max-width: 720px;
    margin: 0 auto;
  }

  .mobile-calendar-month {
    padding: 12px 20px 20px;
  }

  .mobile-calendar-week--horizontal {
    gap: 10px;
    padding: 12px 16px 16px;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .mobile-calendar-week--horizontal .mobile-week-day {
    flex: 1 1 0;
    min-width: 0;
    height: 100%;
    max-height: none;
    display: flex;
    flex-direction: column;
    padding: 10px 8px 6px;
    border: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.12));
    border-radius: 10px;
  }

  .mobile-calendar-week--horizontal .mobile-week-day.is-today {
    border-color: rgba(13, 110, 253, 0.45);
    background: #f0f7ff;
  }

  .dark-theme .mobile-calendar-week--horizontal .mobile-week-day.is-today {
    background: rgba(10, 132, 255, 0.12);
  }

  .mobile-calendar-week--horizontal :deep(.to-do-list-container) {
    flex: 1 1 auto !important;
    width: 100% !important;
    height: 100% !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column;
  }

  .mobile-calendar-week--horizontal :deep(.to-do-list) {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }

  .mobile-calendar-week--horizontal :deep(.fake-drop-zone),
  .mobile-calendar-week--horizontal :deep(.mobile-new-todo-bar) {
    flex: 0 0 auto;
    min-height: 44px;
  }

  .mobile-calendar-week--horizontal :deep(.weekly-to-do-header) {
    margin-bottom: 6px;
    padding: 0 2px;
  }

  .mobile-calendar-week--horizontal :deep(.weekly-to-do-header h4) {
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 0;
  }

  .mobile-calendar-week--horizontal :deep(.weekly-to-do-subheader) {
    display: none;
  }

  .mobile-calendar-week--horizontal :deep(.header-menu-icons) {
    font-size: 18px;
    min-width: 28px;
    min-height: 28px;
  }

  .mobile-calendar-week--horizontal :deep(.mobile-todo-item) {
    min-height: 40px;
  }

  .mobile-calendar-week--horizontal :deep(.mobile-todo-text) {
    font-size: 14px;
  }
}

.dark-theme .mobile-week-day {
  background: var(--m-surface, #1c1c1e);
}

.mobile-week-day.is-today {
  border-color: rgba(13, 110, 253, 0.35);
  box-shadow: 0 0 0 1px rgba(13, 110, 253, 0.12);
}

.mobile-calendar-week :deep(.to-do-list-container) {
  flex: 0 0 auto !important;
  width: 100% !important;
  max-width: 100%;
  height: auto !important;
  min-height: 120px;
}

.mobile-calendar-week :deep(.fake-drop-zone) {
  min-height: 64px;
}

.mobile-calendar-week :deep(.fake-lines) {
  min-height: 40px;
}

/* datepicker 不能占顶栏 flex，否则会把日期挤成竖排；输入框隐藏，弹层居中浮在下方 */
.mobile-calendar-datepicker-host {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  height: 0;
  z-index: 40;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.mobile-calendar-datepicker-host :deep(.v3dp__datepicker) {
  position: relative;
  pointer-events: auto;
}

.mobile-calendar-datepicker-host :deep(.v3dp__input_wrapper) {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  opacity: 0;
  pointer-events: none;
}

.mobile-calendar-datepicker-host :deep(.v3dp__popout) {
  z-index: 41;
  left: 50%;
  transform: translateX(-50%);
}
</style>
